import { BREAKPOINTS, LoadingIndicator, Size } from "@laerdal/life-react-components";
import axios from "axios";
import StepperWizard from "../../commonComponents/StepperWizard";
import { MouseEvent, useEffect, useState } from "react";
import { PageWidthFull } from "../../commonComponents/PageWidthFull";
import './styles.css'
import { ProductLineOnboardingDto } from "../../model/dto/productLineOnboardingDto";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ProductApi from "../../services/api/ProductApi";
import { selectMarket, selectTranslateWithAi } from "../../store/slices/globalConfigSlice";
import { useSelector } from "react-redux";
import { marketList } from "../../model/constants/marketContants";
import { useTranslation } from "react-i18next";
import Troubleshooting from "./components/troubleshooting";
import { posthog } from "posthog-js";
import PaligoContent from "../../commonComponents/PaligoContent";

interface StepperWizardState {
  title: string;
  description?: string;
  steps: {
    stepLabel: string;
    htmlContent: string;
  }[],
  currentStep: number;
}

const OnboardingPage = () => {
  const { market, productLink } = useParams<{ market: string | undefined, productLink: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation('translation');

  const [productLineOnboardingLoading, setProductLineOnboardingLoading] = useState<boolean>(true);
  const [productLineOnboardingDetails, setProductLineOnboardingDetails] = useState<ProductLineOnboardingDto | undefined>(undefined);

  const currentMarket = useSelector(selectMarket);
  const translateWithAi = useSelector(selectTranslateWithAi);
  const [pageOpenedAt] = useState(() => Date.now());

  // Read state directly from URL params
  const stepParam = searchParams.get('step');
  const currentStep = stepParam ? parseInt(stepParam, 10) : 1;
  const isTroubleshooting = searchParams.get('isTroubleshooting') === 'true';

  const state: StepperWizardState = {
    title: t('Setup your manikin'),
    description: t('Easy step-by-step guide to get your manikin ready for use.'),
    steps: productLineOnboardingDetails?.stepsContent.map((stepContent, index) => ({
      stepLabel: `${index + 1}`,
      htmlContent: stepContent
    })) || [],
    currentStep,
  };

  useEffect(() => {
    posthog?.capture?.('SUPP-OnboardingPage WizardOpened', { product_link: productLink });
  }, []);

  const isLastStep = state.currentStep === state.steps.length;
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setProductLineOnboardingLoading(true);
    setProductLineOnboardingDetails(undefined);

    if (productLink) {
      ProductApi
        .GetProductLineOnboardingDetails(productLink, translateWithAi ? marketList[currentMarket].locale : undefined, signal)
        .then((data) => {
          setProductLineOnboardingLoading(false);
          setProductLineOnboardingDetails(data);
        })
        .catch((error) => {
          if (axios.isCancel(error)) return;
          if (axios.isAxiosError(error) && error.response?.status === 404) {
            navigate('..', { replace: true, relative: 'path' });
            return;
          }
          setProductLineOnboardingLoading(false);
        })
    }

    return () => {
      controller.abort();
    };
  }, [productLink, translateWithAi, currentMarket]);

  const onBack = () => {
    if (state.currentStep > 1) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('step', (state.currentStep - 1).toString());
      setSearchParams(newParams, { replace: true });
    } else {
      navigate(-1);
    }
  }

  const onContinue = () => {
    posthog?.capture?.(`SUPP-OnboardingPage WizardStepCompleted - ${currentStep}`, {
      product_link: productLink
    });

    if (state.currentStep < state.steps.length) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('step', (state.currentStep + 1).toString());
      setSearchParams(newParams, { replace: true });
    } else {
      posthog?.capture?.('SUPP-OnboardingPage WizardFinished', {
        product_link: productLink,
        seconds_to_complete: Math.round((Date.now() - pageOpenedAt) / 1000) 
      });

      navigate('..', {
        replace: true,
        state: { setupCompleted: true },
        relative: 'path'
      });
    }
  }

  const onHelp = () => {
    posthog?.capture?.('SUPP-OnboardingPage HelpClicked', {
      current_step: currentStep,
      product_link: productLink
    });

    if (productLineOnboardingDetails?.troubleshooting && productLineOnboardingDetails?.troubleshooting.trim().length > 0) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('isTroubleshooting', 'true');
      setSearchParams(newParams, { replace: true });
    } else {
      reportProblem()
    }
  };

  const reportProblem = () => {
    posthog?.capture?.('SUPP-OnboardingPage ReportProblem', {
      current_step: currentStep,
      product_link: productLink
    });
    navigate(market ? `/${market}/case/create/technical` : '/case/create/technical');
  }

  // Intercept clicks on in-app `/articles/...` links inside Paligo-rendered HTML so navigation
  // stays inside the SPA (no full page reload). External links, modifier-clicks, target="_blank"
  // and any non-article paths fall through to default browser behaviour.
  const handleContentClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const anchor = (e.target as HTMLElement | null)?.closest?.('a');
    if (!anchor) return;
    if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;

    const href = anchor.getAttribute('href');
    if (!href) return;

    // Only intercept root-relative article links (with or without a 2-char market prefix).
    if (!/^\/(?:[a-z]{2}\/)?articles\//.test(href)) return;

    e.preventDefault();
    posthog?.capture?.('SUPP-OnboardingPage InContentLinkClick', {
      product_link: productLink,
      target: href,
    });
    navigate(href);
  };

  return (
    <>
      {productLineOnboardingLoading &&
        <LoadingIndicator />
      }
      {productLineOnboardingDetails &&
        <PageWidthFull $useMaxWidth={true} $maxWidth={1024} >
          {isTroubleshooting &&
            <Troubleshooting
              htmlContent={productLineOnboardingDetails.troubleshooting!}
              onDismiss={() => {
                const newParams = new URLSearchParams(searchParams);
                newParams.delete('isTroubleshooting');
                setSearchParams(newParams, { replace: true });
              }}
              onReportProblem={reportProblem}
            />
          }
          {!isTroubleshooting &&
            <StepperWizard
              steps={state.steps.map(step => step.stepLabel)}
              currentStep={state.currentStep}
              title={state.title}
              description={state.description}
              imageUrl={productLineOnboardingDetails.imageUrl}
              onBack={onBack}
              showLastStep={true}
              children={<PaligoContent onClick={handleContentClick} dangerouslySetInnerHTML={{ __html: state.steps[state.currentStep - 1].htmlContent }}></PaligoContent>}
              actions={[{
                id: 'i-need-help',
                title: t('I need help'),
                variant: 'secondary',
                onClick: onHelp,
                size: Size.Medium,
              }, {
                id: isLastStep ? 'finish-setup' : 'continue',
                title: isLastStep ? t('Finish setup') : t('Continue'),
                variant: 'primary',
                size: Size.Medium,
                onClick: onContinue,
              }]}
            />
          }
        </PageWidthFull>
      }
    </>
  );
};

export default OnboardingPage;